/**
 * Settings card "خانة السوبر (العملات)" (C21 — D02 §5, D-021, D-066, D-069, D-126; engineer decision D-244): binds
 * `CurrenciesCard` (packages/ui, SuperCurrenciesSettings.tsx) to `GET /currencies/settings`, `POST / PATCH /currencies`,
 * `POST /currencies/delete` (Super Manager, own PIN), `PUT /rates` (buy and sell, D-126) and the single vault switch
 * `currencies.show_vault` (`PUT /settings`, Super Manager, PIN; D-069 (1): hides the screens only — the old transfer /
 * clear panel is gone). Every change is audited by the server (D-134).
 */
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CurrenciesCard, SettingsCardFrame, formatWithCommas, parseCommas, toEnglishDigits, type NewCurrencyValues } from "@faylasuf/ui";
import type { PinProof } from "../../api/types.js";
import { useApp, useSession } from "../../app/context.js";
import { errorMessage, isSessionLost } from "../../app/errors.js";
import { usePinGate } from "../../app/pin-gate.js";
import { keys } from "../../app/query.js";
import { useToast } from "../../app/toast.js";

const QUOTE_UNITS = ["1", "10", "100", "1000", "10000", "100000", "1000000"];
const clean = (s: string) => toEnglishDigits(parseCommas(s.trim()));

export function CurrenciesContainer({ customIcon }: { customIcon?: string }) {
  const { t, source, locale } = useApp();
  const { session, sessionLost } = useSession();
  const toast = useToast();
  const qc = useQueryClient();
  const gate = usePinGate();
  const [busy, setBusy] = useState(false);
  const restricted = session.user.roleBuiltin !== "super_manager"; // D-069: the Super Manager's card (old :68-75)
  const q = useQuery({ queryKey: keys.currencySettings, queryFn: () => source.currencySettings(), enabled: !restricted });
  const settings = useQuery({ queryKey: keys.settings, queryFn: () => source.settings(), enabled: !restricted });
  for (const e of [q.error, settings.error]) if (e && isSessionLost(e)) sessionLost();
  const vaultVisible = settings.data?.["currencies.show_vault"]?.value !== false;

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: keys.currencySettings });
    void qc.invalidateQueries({ queryKey: keys.currencies });
    void qc.invalidateQueries({ queryKey: keys.settings });
    void qc.invalidateQueries({ queryKey: keys.rates });
  };
  const run = async (action: (proof: PinProof) => Promise<unknown>, message: string, successKey: string): Promise<boolean> => {
    setBusy(true);
    try {
      const r = await gate.withPin(action, { message });
      if (r === undefined) return false;
      toast(t(successKey), "success");
      refresh();
      return true;
    } catch (e) {
      if (isSessionLost(e)) sessionLost();
      else toast(errorMessage(t, e), "error");
      return false;
    } finally {
      setBusy(false);
    }
  };

  const rows = (q.data?.currencies ?? []).map((c) => ({
    code: c.code,
    name: locale === "ar" ? c.nameAr : c.nameEn,
    symbol: c.symbol,
    multipleLabel: formatWithCommas(c.multiple),
    quoteUnitLabel: formatWithCommas(c.quoteUnit),
    visible: c.visible,
    suspended: c.suspended,
    buy: c.rates?.buy ?? "",
    sell: c.rates?.sell ?? "",
  }));
  const nameOf = (code: string) => rows.find((r) => r.code === code)?.name ?? code;

  const add = async (v: NewCurrencyValues) => {
    const code = v.code.trim().toUpperCase();
    if (!/^[A-Z0-9]{3,8}$/.test(code)) return { type: "error" as const, message: t("error.CURRENCY_CODE_INVALID") };
    const bills = v.bills.split(",").map(clean).filter((b) => /^\d+(\.\d+)?$/.test(b) && /[1-9]/.test(b));
    let failure: string | null = null;
    setBusy(true);
    try {
      const r = await gate.withPin(
        (proof) => source.addCurrency({ code, nameAr: v.nameAr.trim(), nameEn: v.nameEn.trim(), symbol: v.symbol.trim(), quoteUnit: v.quoteUnit, multiple: clean(v.multiple) || "1", bills, buy: clean(v.buy), sell: clean(v.sell), pin: proof.pin ?? "" }),
        { message: t("pin.message_currency_add", { code }) },
      );
      if (r === undefined) return null;
    } catch (e) {
      if (isSessionLost(e)) sessionLost();
      failure = errorMessage(t, e);
    } finally {
      setBusy(false);
    }
    if (failure) return { type: "error" as const, message: failure };
    refresh();
    return { type: "success" as const, message: t("cur.added", { code }) };
  };

  return (
    <SettingsCardFrame cardKey="super_currencies" {...(customIcon ? { customIcon } : {})}>
      <CurrenciesCard
        restricted={restricted}
        vaultVisible={vaultVisible}
        onToggleVault={() => void run((proof) => source.setSetting("currencies.show_vault", !vaultVisible, proof.pin ?? ""), t(vaultVisible ? "pin.message_currency_vault_off" : "pin.message_currency_vault_on"), "toast.saved")}
        rows={rows}
        quoteUnits={QUOTE_UNITS}
        onAdd={add}
        onSaveRates={(code, buy, sell) => run((proof) => source.setRate(code, clean(buy), clean(sell), proof), t("pin.message_currency_rates", { name: nameOf(code), buy: formatWithCommas(clean(buy)), sell: formatWithCommas(clean(sell)) }), "toast.rates_updated")}
        onToggleVisible={(code) => {
          const r = rows.find((x) => x.code === code);
          void run((proof) => source.updateCurrency({ code, visible: !r?.visible, pin: proof.pin ?? "" }), t(r?.visible ? "pin.message_currency_hide" : "pin.message_currency_show", { name: nameOf(code) }), "toast.saved");
        }}
        onToggleSuspended={(code) => {
          const r = rows.find((x) => x.code === code);
          void run((proof) => source.updateCurrency({ code, suspended: !r?.suspended, pin: proof.pin ?? "" }), t(r?.suspended ? "pin.message_currency_activate" : "pin.message_currency_suspend", { name: nameOf(code) }), "toast.saved");
        }}
        onDelete={(code) => void run((proof) => source.deleteCurrency(code, proof.pin ?? ""), t("pin.message_currency_delete", { name: nameOf(code) }), "cur.deleted")}
        busy={busy}
      />
      {gate.dialog}
    </SettingsCardFrame>
  );
}
